import { type UnwrapRef } from "vue";

const featureFlags = computed(() => ({
  messagesUi:
    env.VITE_ENABLE_FEATURE__MESSAGES_UI === "1" &&
    env.VITE_ENABLE_FEATURE__INTEGRATION_EMAIL === "1",
  multiUser:
    env.VITE_ENABLE_FEATURE__ADD_ACCOUNT_USERS === "1" &&
    !!env.VITE_MULTIPLE_USER_ENABLED_PRICE_ID,
  freeTrialNoStripe: env.VITE_ENABLE_FEATURE__FREE_TRIAL_WITHOUT_STRIPE === "1",
  integrationFacebook: env.VITE_ENABLE_FEATURE__INTEGRATION_FACEBOOK === "1",
  integrationInstagram: env.VITE_ENABLE_FEATURE__INTEGRATION_INSTAGRAM === "1",
  integrationWordpress: env.VITE_ENABLE_FEATURE__INTEGRATION_WORDPRESS === "1",
  integrationTeams: env.VITE_ENABLE_FEATURE__INTEGRATION_TEAMS === "1",
  integrationEmail: env.VITE_ENABLE_FEATURE__INTEGRATION_EMAIL === "1",
  integrationZapier: env.VITE_ENABLE_FEATURE__INTEGRATION_ZAPIER === "1",
  integrationTwitter: env.VITE_ENABLE_FEATURE__INTEGRATION_TWITTER === "1",
  integrationLinkedin: env.VITE_ENABLE_FEATURE__INTEGRATION_LINKEDIN === "1",
  integrationYoutube: env.VITE_ENABLE_FEATURE__INTEGRATION_YOUTUBE === "1",
  "integrationGoogle-analytics":
    env.VITE_ENABLE_FEATURE__INTEGRATION_GOOGLE_ANALYTICS === "1",
  integrationTiktok: env.VITE_ENABLE_FEATURE__INTEGRATION_TIKTOK === "1",
  accountFilesUi: env.VITE_ENABLE_FEATURE__ACCOUNT_FILES_UI === "1",
  v2Wrapper: env.VITE_ENABLE_FEATURE__V2_APP_WRAPPER === "1",
  accountDeletion: env.VITE_ENABLE_FEATURE__ACCOUNT_DELETION === "1",
  welcomeTour: env.VITE_ENABLE_FEATURE__WELCOME_TOUR === "1",
  accountUserDeletion: env.VITE_ENABLE_FEATURE__ACCOUNT_USER_DELETION === "1",
  emailMarketing: env.VITE_ENABLE_FEATURE__INTEGRATION_EMAIL_MARKETING === "1",
  contentCalendar: env.VITE_ENABLE_FEATURE_CONTENT_CALENDAR === "1",
  contentEdit: env.VITE_ENABLE_FEATURE_CONTENT_EDIT === "1",
  v2ScrollToBottom: env.VITE_ENABLE_FEATURE_V2_SCROLL_TO_BOTTOM === "1",
  competitorReport: env.VITE_ENABLE_FEATURE__COMPETITOR_REPORT === "1",
  whatsNewUi: env.VITE_ENABLE_FEATURE__WHATSNEW_UI === "1",
}));

export type FeatureFlags = keyof UnwrapRef<typeof featureFlags>;

const featureFlagsBypass = computed<Record<FeatureFlags, string[]>>(() => {
  const integrationsExceptions =
    env.VITE_INTEGRATIONS_EXCEPTIONS_FOR_USERS?.split(",")
      .map(email => email.trim())
      .filter(email => email.length > 0) || [];

  return {
    messagesUi: [],
    multiUser: [],
    freeTrialNoStripe: [],
    integrationFacebook: integrationsExceptions,
    integrationInstagram: integrationsExceptions,
    integrationWordpress: integrationsExceptions,
    integrationTeams: integrationsExceptions,
    integrationEmail: integrationsExceptions,
    integrationZapier: integrationsExceptions,
    integrationTwitter: integrationsExceptions,
    integrationLinkedin: integrationsExceptions,
    integrationTiktok: integrationsExceptions,
    integrationYoutube: integrationsExceptions,
    v2ScrollToBottom: [],
    "integrationGoogle-analytics": integrationsExceptions,
    accountFilesUi: [],
    v2Wrapper: env.VITE_FEATURE_EXCEPTION__V2_APP_WRAPPER?.split(",") || [],
    accountDeletion: [],
    welcomeTour: [],
    accountUserDeletion: [],
    emailMarketing: integrationsExceptions,
    contentCalendar: env.VITE_FEATURE_EXCEPTION__CONTENT_CALENDAR?.split(",") || [],
    contentEdit: env.VITE_FEATURE_EXCEPTION__CONTENT_EDIT?.split(",") || [],
    competitorReport: env.VITE_FEATURE_EXCEPTION__COMPETITOR_REPORT?.split(",") || [],
    whatsNewUi: [],
  };
});

/**
 * checks if a the current user can use a feature even if the flag is disabled. You usually don't have to use this because it is already checked in the `hasFeatureFlag` function.
 * @param feature - The feature flag to check
 * @returns true if the feature flag is bypassed for the user, false otherwise
 */
export function hasFeatureFlagBypass(feature: FeatureFlags[] | FeatureFlags) {
  const features = Array.isArray(feature) ? feature : [feature];
  const filtered = features.filter(f => featureFlagsBypass.value[f] !== undefined);
  if (filtered.length !== features.length) {
    return false;
  }
  const authStore = useAuthStore();
  if (!authStore.user?.email) return false;
  return filtered.every(f => {
    if (!authStore.user?.email) return false; // ts is dumb
    const bypass = featureFlagsBypass.value[f];
    if (!bypass?.length) return false;
    return bypass.includes(authStore.user.email);
  });
}

export function hasFeatureFlag(feature: FeatureFlags[] | FeatureFlags) {
  const features = Array.isArray(feature) ? feature : [feature];
  const filtered = features.filter(f => featureFlags.value[f] !== undefined);
  if (filtered.length !== features.length) {
    // if there was incorrect passed in, fail it
    return false;
  }
  // make sure they are all enabled / bypassed for current user
  return filtered.every(f => featureFlags.value[f] || hasFeatureFlagBypass(f));
}
